// App.jsx or Layout.jsx
import './Events.css';
import React, { useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import mapboxSdk from '@mapbox/mapbox-sdk';
import mbxGeocoding from '@mapbox/mapbox-sdk/services/geocoding';
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';
import mapMarker from './PKC_Map_Marker_75.png';
import logo from './PKC_Logo_Small.JPG';
import InfoCard from './InfoCard';
import EventListView from './EventListView';
import * as turf from '@turf/turf';

function Home2() {
    const mapContainerRef = useRef();
    const mapRef = useRef();
    const [config, setConfig] = useState(null);
    const geocodingClientRef = useRef();
    const [stateGeoJson, setStateGeoJson] = useState(null);
    const [eventData, setEventData] = useState(null);
    const geocoderRef = useRef();
    const [searchTerm, setSearchTerm] = useState('');
    const geocoderContainerRef = useRef(null);
    const [showResults, setShowResults] = useState(false);
    const [searchResults, setSearchResults] = useState([]);
    const geocoderInputRef = useRef(null);
    const resultsPanelRef = useRef(null);
    const noResultsPanelRef = useRef(null);
    const [selectedFeature, setSelectedFeature] = useState(null);
    const [showEventsList, setShowEventsList] = useState(false);

    const originalBounds = [
        [-109.04503689386989, 37.0021309434908],
        [-102.05181504519271, 41.00284889412739],
    ]

    const handleGeoSearchResult = (feature) => {
        setSearchTerm(feature);
        setShowResults(false);

        if (geocoderInputRef.current && feature) {
            geocoderInputRef.current.value = feature.place_name ?? '';
        }

        // find the closest one
        filterGeoJSONByDistance(feature);

    };

    useEffect(() => {
        const fetchConfig = async () => {
            const response = await fetch('config');
            const result = await response.text(); // Await the JSON parsing
            setConfig(result);
        }

        fetchConfig();
    }, []);

    useEffect(() => {
        const handleClickOutside = (event) => {
            const panel = resultsPanelRef.current;
            const noResults = noResultsPanelRef.current;

            if (
                panel &&
                !panel.contains(event.target) &&
                geocoderInputRef.current &&
                !geocoderInputRef.current.contains(event.target)
            ) {
                setShowResults(false);
                return;
            }

            if (
                noResults &&
                noResults.contains(event.target) &&
                geocoderInputRef.current &&
                !geocoderInputRef.current.contains(event.target)
            ) {
                setShowResults(false);
                return;
            }

            if (
                noResults &&
                !noResults.contains(event.target) &&
                geocoderInputRef.current &&
                !geocoderInputRef.current.contains(event.target)
            ) {
                setShowResults(false);
                return;
            }

            if (
                geocoderInputRef.current &&
                geocoderInputRef.current.contains(event.target)
            ) {
                setShowResults(true);
                return;
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);


    useEffect(() => {

        if (!config) return; // Initialize map only once

        mapboxgl.accessToken = config;
        mapRef.current = new mapboxgl.Map({
            container: mapContainerRef.current,
            style: 'mapbox://styles/mapbox/streets-v11',

            //center: [-106.1063329, 37.751663],
            
            bounds: originalBounds, // bounding box (southwest corner, northeast corner)
            // by setting the bounds your map will automatically center around the bounding box
            fitBoundsOptions: {
                zoom: 6
            },
        });

        mapRef.current.addControl(new mapboxgl.NavigationControl(), 'top-left');

        const mapboxClient = mapboxSdk({ accessToken: mapboxgl.accessToken });
        geocodingClientRef.current = mbxGeocoding(mapboxClient);

        const geocoder = new MapboxGeocoder({
            accessToken: mapboxgl.accessToken,
            mapboxgl,
            placeholder: 'Search location',
            marker: false,
            proximity: { longitude: -105.1, latitude: 40.7 }, // Wellington, CO
            render: () => null, // disables dropdown rendering
        });

        geocoderRef.current = geocoder;

        geocoder.addTo(geocoderContainerRef.current);

        // Wait for input to mount
        setTimeout(() => {
            const input = document.querySelector('.mapboxgl-ctrl-geocoder input');
            if (input) geocoderInputRef.current = input;
        }, 0);

        setTimeout(() => {
            const input = document.querySelector('.mapboxgl-ctrl-geocoder input');
            if (input) {
                geocoderInputRef.current = input;

                // Show results panel on focus
                input.addEventListener('focus', () => {
                    setShowResults(true);
                });
            }
        }, 0);

        geocoder.on('result', (e) => {
            handleGeoSearchResult(e.result.center);
        });

        geocoder.on('results', (e) => {
            setSearchResults(e.features);

            setShowResults(true);
        });

        const clearBtn = document.querySelector('.mapboxgl-ctrl-geocoder--button');
        const input = document.querySelector('.mapboxgl-ctrl-geocoder input');

        if (clearBtn && input) {
            const handleClear = () => {
                // Clear your custom results
                setSearchResults([]);
                setShowResults(false);
                setSelectedFeature(null);
                // Optional: clear input manually
                input.value = '';
            };

            clearBtn.addEventListener('click', handleClear);

        }

        mapRef.current.on('load', () => {

            const fetchStateGeoJSON = async () => {
                const response = await fetch('stategeojson');
                const result = await response.json(); // Await the JSON parsing
                setStateGeoJson(result);
            }

            const fetchEventData = async () => {
                const response = await fetch('events');
                const result = await response.json(); // Await the JSON parsing

                const denver = turf.point([-104.991531, 39.742043]); // [lng, lat]

                result?.features?.forEach((feature) => {
                    const distance = turf.distance(denver, feature, { units: 'kilometers' });
                    feature.properties.distance = distance;
                });

                result?.features?.sort((a, b) => a.properties.distance - b.properties.distance);

                setEventData(result);
            }

            fetchStateGeoJSON();
            fetchEventData();
        });
    }, [config]);

    useEffect(() => {
        if (!mapRef.current || !stateGeoJson) return;

        if (mapRef.current.getSource('state')) {
            mapRef.current.getSource('state').setData(stateGeoJson);
        } else {
            mapRef.current.addSource('state', {
                type: 'geojson',
                data: stateGeoJson
            });

            mapRef.current.addLayer({
                id: 'line-bounding-box',
                type: 'line',
                paint: {
                    'line-color': '#094877',
                    'line-width': 1,
                    'line-opacity': 0.9
                },
                source: 'state'
            });

        }
    }, [stateGeoJson]);

    useEffect(() => {
        if (!mapRef.current || !eventData) return;

        //mapMarker

        const addLayer = () => {

            // instead of dynamically adding markers, we will get GeoJson from the BE and set source
            if (mapRef.current.getSource('event-markers')) {
                mapRef.current.getSource('event-markers').setData(eventData);
            } else {

                mapRef.current.addSource('event-markers', {
                    type: 'geojson',
                    data: eventData
                });

                mapRef.current.loadImage(mapMarker, (error, image) => {
                    if (error) throw error;
                    if (!mapRef.current.hasImage('pkc-icon')) {
                        mapRef.current.addImage('pkc-icon', image);
                    }

                });

                mapRef.current.addLayer({
                    id: 'event-markers-layer',
                    type: 'symbol',
                    source: 'event-markers',
                    layout: {
                        'icon-image': 'pkc-icon',
                        'icon-size': 0.5,
                        'icon-allow-overlap': true,
                        'text-field': ['get', 'title'],
                        'text-offset': [0, 1.5],
                        'text-anchor': 'top',
                    },
                    paint: {
                        'text-color': '#000000',
                    },
                });

                mapRef.current.on('click', 'event-markers-layer', (e) => {
                    const feature = e.features[0];

                    setSelectedFeature(feature.properties);
                });

                // Optional: change cursor on hover
                mapRef.current.on('mouseenter', 'event-markers-layer', () => {
                    mapRef.current.getCanvas().style.cursor = 'pointer';
                });
                mapRef.current.on('mouseleave', 'event-markers-layer', () => {
                    mapRef.current.getCanvas().style.cursor = '';
                });


                mapRef.current.moveLayer('event-markers-layer');
            }
        };

        if (mapRef.current.isStyleLoaded()) {
            addLayer();
        } else {
            mapRef.current.once('idle', addLayer);
        }


    }, [eventData]);

    const handleMapShow = () => {
        setShowEventsList(false);
    };

    const handleListShow = () => {
        setShowEventsList(true);
    };

    const filterGeoJSONByDistance = (centerPoint) => {
        const center = turf.point(centerPoint.center); // [lng, lat]

        eventData?.features?.forEach((feature) => {
            const distance = turf.distance(center, feature, { units: 'kilometers' });
            feature.properties.distance = distance;
        });

        eventData?.features?.sort((a, b) => a.properties.distance - b.properties.distance);


        // fly to the closest one

        const closestEvent = turf.nearestPoint(center, eventData);

        setSelectedFeature(closestEvent.properties);

        mapRef.current.flyTo({
            center: closestEvent?.geometry?.coordinates,
            zoom: 8
        });
    };


    return (
        <div className="page-container">



            <header className="header-events">
                <div className="header-events-logo-section">
                    <img src={logo} alt="Logo" className="header-events-logo" />
                </div>
                <div className="header-events-color-section">
                    <h2 className="header-events-title">Protect Kids Colorado</h2>
                    <h2 className="header-events-title-small">Upcoming Events</h2>
                </div>
            </header>


            <section className="search-bar">
                <div className="geocoder-container">
                    <div className="geocoder-container-ref" ref={geocoderContainerRef} />
                    <button className="geo-button" onClick={handleMapShow} >
                        <i className="far fa-map"></i>
                    </button>
                    <button className="geo-button" onClick={handleListShow}>
                        <i className="far fa-list-alt"></i>
                    </button>
                </div>

                
            </section>
            <main className="main-content">
                
                        <div className="map-container" ref={mapContainerRef} id="map"></div>
                        {showResults && (
                            <div className="results-overlay">


                                {searchResults.length === 0 ? (
                                    <div className="no-results" ref={noResultsPanelRef}>
                                        <div className="icon-row">
                                            <span className="icon">
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    width="16"
                                                    height="16"
                                                    fill="currentColor"
                                                    viewBox="0 0 16 16"
                                                >
                                                    <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85zm-5.242 1.656a5.5 5.5 0 1 1 0-11 5.5 5.5 0 0 1 0 11z" />
                                                </svg>
                                            </span>
                                        </div>
                                        <div className="text-row">START YOUR SEARCH</div>
                                        <div className="subtext-row">Search by address, city, zip</div>
                                    </div>

                                ) : (
                                    <ul className="results-list" ref={resultsPanelRef}>
                                        {searchResults.map((feature) => (
                                            <li key={feature.id} className="result-item" onClick={() => handleGeoSearchResult(feature)}>
                                                <span className="icon">
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        width="16"
                                                        height="16"
                                                        fill="currentColor"
                                                        viewBox="0 0 16 16"
                                                    >
                                                        <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85zm-5.242 1.656a5.5 5.5 0 1 1 0-11 5.5 5.5 0 0 1 0 11z" />
                                                    </svg>
                                                </span>
                                                <span className="place-name">{feature.place_name}</span>
                                            </li>

                                        ))}
                                    </ul>
                                )}
                            </div>
                        )}
                        {selectedFeature && (
                            <div className="bottom-panel">
                                <InfoCard key={1} item={selectedFeature} />
                            </div>
                        )}
                        {showEventsList && (
                            <div className="results-overlay">
                                <EventListView events={eventData}></EventListView>
                        {showResults && (
                            <div className="results-overlay">


                                {searchResults.length === 0 ? (
                                    <div className="no-results" ref={noResultsPanelRef}>
                                        <div className="icon-row">
                                            <span className="icon">
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    width="16"
                                                    height="16"
                                                    fill="currentColor"
                                                    viewBox="0 0 16 16"
                                                >
                                                    <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85zm-5.242 1.656a5.5 5.5 0 1 1 0-11 5.5 5.5 0 0 1 0 11z" />
                                                </svg>
                                            </span>
                                        </div>
                                        <div className="text-row">START YOUR SEARCH</div>
                                        <div className="subtext-row">Search by address, city, zip</div>
                                    </div>

                                ) : (
                                    <ul className="results-list" ref={resultsPanelRef}>
                                        {searchResults.map((feature) => (
                                            <li key={feature.id} className="result-item" onClick={() => handleGeoSearchResult(feature)}>
                                                <span className="icon">
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        width="16"
                                                        height="16"
                                                        fill="currentColor"
                                                        viewBox="0 0 16 16"
                                                    >
                                                        <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85zm-5.242 1.656a5.5 5.5 0 1 1 0-11 5.5 5.5 0 0 1 0 11z" />
                                                    </svg>
                                                </span>
                                                <span className="place-name">{feature.place_name}</span>
                                            </li>

                                        ))}
                                    </ul>
                                )}
                            </div>
                        )}
                            </div>
                        )}
                
            </main>
        </div>
    );
}

export default Home2;
